import { useCallback, useEffect, useRef, useState } from 'react';
import Announcements from './components/Announcements';
import AppShare from './components/AppShare';
import Forum from './components/Forum';
import FollowUs from './components/FollowUs';
import Home from './components/Home';
import Login from './components/Login';
import Profile from './components/Profile';
import Quizzes from './components/Quizzes';
import Register from './components/Register';
import Resources from './components/Resources';
import Simulations from './components/Simulations';
import { UserProvider, useUser } from './contexts/UserContext';
import './frontoffice-design.css';
import './frontoffice-mobilefirst.css';

// Lightweight, accessible vector pictograms. Kept local to avoid dependencies.
const shapes = {
  home: <><path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><path d="M9 21v-8h6v8" /></>,
  resources: <><path d="M4 5.5c3.2-1.6 5.8-1.6 8 0 2.2-1.6 4.8-1.6 8 0V20c-3.2-1.6-5.8-1.6-8 0-2.2-1.6-4.8-1.6-8 0z" /><path d="M12 5.5V20" /></>,
  quizzes: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="m9 10 2 2 4-4M9 16h6" /></>,
  simulations: <><circle cx="12" cy="13" r="8" /><path d="M12 8v5l3 2M9 2h6" /></>,
  announcements: <><path d="M4 10v4l11 4V6L4 10ZM15 9l4-2v10l-4-2" /><path d="m7 15 2 6h3l-2-5" /></>,
  forum: <><path d="M20 11.5a7.5 7.5 0 0 1-7.5 7.5H6l-3 3v-9A7.5 7.5 0 1 1 20 11.5z" /><path d="M7 11h10M7 15h6" /></>,
  back: <><path d="m14.5 5-7 7 7 7" /></>,
  moon: <><path d="M20.4 15.4A8.5 8.5 0 0 1 8.6 3.6a8.5 8.5 0 1 0 11.8 11.8Z" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" /></>,
  chevron: <><path d="m6 9 6 6 6-6" /></>,
};
function UIIcon({ name, size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">{shapes[name]}</svg>
  );
}

function HeaderAuth({ currentTab, navigateToTab, goHome }) {
  const { currentUser, logout } = useUser();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return undefined;
    const closeOnOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) setShowMenu(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowMenu(false);
    };
    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showMenu]);

  if (!currentUser) {
    return (
      <div className="cb-header-auth">
        {currentTab !== 'login' && (
          <button type="button" className="cb-header-login"
            onClick={() => navigateToTab('login')}>Connexion</button>
        )}
        {currentTab !== 'register' && (
          <button type="button" className="cb-button cb-button--header"
            onClick={() => navigateToTab('register')}>S'inscrire <span aria-hidden="true">↗</span></button>
        )}
      </div>
    );
  }

  return (
    <div className="cb-account" ref={menuRef}>
      <button type="button" className="cb-account-trigger"
        onClick={() => setShowMenu((open) => !open)}
        aria-haspopup="menu" aria-expanded={showMenu}>
        <span className="cb-account-avatar"><UIIcon name="user" size={18} /></span>
        <span className="cb-account-name">
          {currentUser.pseudonym || currentUser.email?.split('@')[0] || 'Profil'}
        </span>
        <UIIcon name="chevron" size={15} />
      </button>
      {showMenu && (
        <div className="cb-account-menu" role="menu">
          <p className="cb-account-menu-label">Mon espace</p>
          <button type="button" role="menuitem" onClick={() => {
            navigateToTab('profile');
            setShowMenu(false);
          }}>Mon Profil</button>
          <button type="button" role="menuitem" onClick={() => {
            navigateToTab('billing');
            setShowMenu(false);
          }}>Mon abonnement</button>
          <div className="cb-account-divider" />
          <button type="button" className="cb-account-logout" role="menuitem" onClick={() => {
            setShowMenu(false);
            logout();
            goHome();
          }}>Se déconnecter</button>
        </div>
      )}
    </div>
  );
}

function AppContent() {
  const { currentUser, accessProfile } = useUser();
  const [currentTab, setCurrentTab] = useState(() =>
    new URLSearchParams(window.location.search).get('ref') ? 'register' : 'home'
  );
  const [history, setHistory] = useState(['home']);
  const [premiumReturnTab, setPremiumReturnTab] = useState(null);
  const [billingLoginReturn, setBillingLoginReturn] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [showBackButton, setShowBackButton] = useState(false);
  const [goUpLevelCallback, setGoUpLevelCallback] = useState(null);
  const [goBackTabCallback, setGoBackTabCallback] = useState(null);
  const [currentNavLevel, setCurrentNavLevel] = useState(0);

  const [showUI, setShowUI] = useState(true);
  const hideTimeoutRef = useRef(null);

  // Keep app navigation stable; retain a distraction-free exam state only.
  const resetUITimer = useCallback(() => {
    setShowUI(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    const activeExam = document.querySelector('.exam-session');
    if (activeExam) {
      hideTimeoutRef.current = setTimeout(() => setShowUI(false), 4000);
    }
  }, []);

  useEffect(() => () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, []);

  // A tap during a quiz can bring navigation back without hiding it on
  // ordinary screens or when clicking a form input / primary action.
  const handleContentClick = () => {
    if (document.querySelector('.exam-session')) {
      resetUITimer();
    } else if (!showUI) {
      // Restore ordinary navigation as soon as the exam view ends.
      setShowUI(true);
    }
  };

  useEffect(() => {
    setShowUI(true);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, [currentTab]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const navigateToTab = (tab) => {
    if (tab !== currentTab) {
      setShowBackButton(false);
      setGoUpLevelCallback(null);
      setGoBackTabCallback(null);
      setCurrentNavLevel(0);
      if (tab !== 'home') {
        setHistory((previousHistory) => [...previousHistory, tab]);
      }
      setCurrentTab(tab);
      window.scrollTo(0, 0);
    }
  };

  // A Premium paywall should lead to the candidate's actual subscription
  // screen, and an anonymous visitor should be guided through login first.
  const openPremiumAccess = () => {
    setPremiumReturnTab(currentTab);
    if (!currentUser) {
      setBillingLoginReturn(true);
      navigateToTab('login');
    } else {
      navigateToTab('billing');
    }
  };

  const goHome = () => {
    setHistory(['home']);
    setShowBackButton(false);
    setGoUpLevelCallback(null);
    setGoBackTabCallback(null);
    setCurrentNavLevel(0);
    setCurrentTab('home');
    window.scrollTo(0, 0);
  };

  const goUpLevel = () => {
    if (goUpLevelCallback) {
      goUpLevelCallback();
    } else if (goBackTabCallback) {
      goBackTabCallback();
    }
  };

  const goBack = () => {
    if (currentNavLevel > 1 && goUpLevelCallback) {
      goUpLevel();
    } else if (currentNavLevel === 1 && goUpLevelCallback) {
      goUpLevel();
    } else if (goBackTabCallback) {
      goBackTabCallback();
    } else if (history.length > 1) {
      const newHistory = history.slice(0, -1);
      const previousTab = newHistory[newHistory.length - 1];
      setHistory(newHistory);
      setCurrentTab(previousTab);
      setShowBackButton(newHistory.length > 1);
      window.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  };

  const canGoBack = history.length > 1 || showBackButton;

  const registerGoUpLevel = (callback, level) => {
    setGoUpLevelCallback(() => callback);
    setCurrentNavLevel(level);
    setShowBackButton(true);
  };

  const registerGoBackTab = (callback) => {
    setGoBackTabCallback(() => callback);
  };

  const contentAccessKey = `${currentUser?.id || 'anonymous'}:${
    accessProfile?.tier || 'free'
  }:${accessProfile?.premiumExpiresAt || 'no-expiry'}`;

  const renderTab = () => {
    switch (currentTab) {
      case 'home':
        return <Home onNavigate={navigateToTab} />;
      case 'quizzes':
        return (
          <Quizzes
            key={`quizzes:${contentAccessKey}`}
            onGoUpLevel={registerGoUpLevel}
            onGoBackTab={registerGoBackTab}
            onOpenPremium={openPremiumAccess}
          />
        );
      case 'simulations':
        return (
          <Simulations
            key={`simulations:${contentAccessKey}`}
            onGoUpLevel={registerGoUpLevel}
            onGoBackTab={registerGoBackTab}
            onOpenPremium={openPremiumAccess}
          />
        );
      case 'announcements':
        return <Announcements />;
      case 'forum':
        return <Forum onGoUpLevel={registerGoUpLevel} onGoBackTab={registerGoBackTab} />;
      case 'profile':
      case 'billing':
        return <Profile
          focusSection={currentTab === 'billing' ? 'subscription' : 'overview'}
          onBackToContent={premiumReturnTab && premiumReturnTab !== 'profile'
            ? () => navigateToTab(premiumReturnTab)
            : null}
        />;
      case 'resources':
        return (
          <Resources
            key={`resources:${contentAccessKey}`}
            onGoUpLevel={registerGoUpLevel}
            onGoBackTab={registerGoBackTab}
            onOpenPremium={openPremiumAccess}
          />
        );
      case 'login':
        return <Login onSwitchToRegister={() => navigateToTab('register')} onLoginSuccess={() => {
          const destination = billingLoginReturn ? 'billing' : 'profile';
          setBillingLoginReturn(false);
          navigateToTab(destination);
        }} />;
      case 'register':
        return <Register onSwitchToLogin={() => navigateToTab('login')} onRegisterSuccess={() => navigateToTab('profile')} />;
      default:
        return <Home onNavigate={navigateToTab} />;
    }
  };

  const isExam = currentTab === 'quizzes' || currentTab === 'simulations';
  const navigation = [
    ['home', 'Accueil', 'home'],
    ['resources', 'Ressources', 'resources'],
    ['quizzes', 'Quiz', 'quizzes'],
    ['simulations', 'Simulations', 'simulations'],
    ['announcements', 'Annonces', 'announcements'],
  ];

  return (
    <div className={'cb-shell' + (currentTab === 'home' && currentUser ? ' cb-shell--connected-home' : '')}>
      {(showUI || !isExam) && (
        <header className="cb-topbar">
          <div className="cb-topbar-inner">
            <div className="cb-topbar-left">
              <button type="button" className="cb-brand" onClick={goHome}
                aria-label="ConcoursBF — aller à l'accueil">
                <span className="cb-brand-mark" aria-hidden="true">
                  <svg viewBox="0 0 48 48" width="30" height="30" fill="none" focusable="false">
                    <path d="M24 13c-6-5-12-6-18-4v26c7-2 12-1 18 4 6-5 11-6 18-4V9c-6-2-12-1-18 4Z"
                      stroke="white" strokeWidth="3" strokeLinejoin="round" />
                    <path d="M24 14v25" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    <path d="M28 10c3-5 7-6 11-5-1 5-4 8-10 8"
                      fill="#87F2D0" stroke="#87F2D0" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="cb-brand-copy"><strong>Concours<span>BF</span></strong>
                  <small>Votre réussite se prépare ici</small></span>
              </button>
              {canGoBack && (
                <button type="button" className="cb-icon-button cb-back" onClick={goBack}
                  aria-label="Retour" title="Revenir à l'étape précédente">
                  <UIIcon name="back" size={20} />
                  <span>Retour</span>
                </button>
              )}
            </div>
            <div className="cb-header-actions">
              <button type="button" className={currentTab === 'forum'
                ? 'cb-icon-button is-active' : 'cb-icon-button'}
                aria-label="Forum" title="Échanger avec la communauté"
                onClick={() => navigateToTab('forum')}>
                <UIIcon name="forum" size={21} /><span className="cb-action-text">Forum</span>
              </button>
              <button type="button" onClick={toggleTheme} className="cb-icon-button cb-theme-switch"
                aria-label={theme === 'light' ? 'Activer le mode sombre' : 'Activer le mode clair'}
                title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}>
                <UIIcon name={theme === 'light' ? 'moon' : 'sun'} size={19} />
              </button>
              <div className="cb-share-action"><AppShare /></div>
              <HeaderAuth currentTab={currentTab} navigateToTab={navigateToTab} goHome={goHome} />
            </div>
          </div>
        </header>
      )}

      {(showUI || !isExam) && (
        <nav className="cb-primary-nav" aria-label="Navigation principale">
          <div className="cb-primary-nav-inner">
            {navigation.map(([key, label, icon]) => (
              <button type="button" key={key} onClick={() => key === 'home' ? goHome() : navigateToTab(key)}
                className={currentTab === key ? 'cb-nav-item is-active' : 'cb-nav-item'}
                aria-label={label}
                title={label}
                aria-current={currentTab === key ? 'page' : undefined}>
                <UIIcon name={icon} size={19} /><span className="cb-nav-label">{label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}

      <main id="main-content"
        className={'cb-main' + (currentTab === 'home' && currentUser ? ' cb-main--connected-home' : '')}
        onClick={handleContentClick}>
        {renderTab()}
      </main>

      <footer className="cb-page-footer" aria-label="Informations sur ConcoursBF">
        <div className="cb-page-footer-copy" aria-label="Nous contacter">
          <strong>Nous contacter</strong>
          <div className="cb-footer-contact-icons" aria-label="Moyens de contact">
            <span className="cb-footer-contact-icon cb-footer-contact-icon--email"
              role="img" aria-label="E-mail" title="E-mail">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                fill="none" stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 7 9-7" />
              </svg>
            </span>
            <span className="cb-footer-contact-icon cb-footer-contact-icon--whatsapp"
              role="img" aria-label="WhatsApp" title="WhatsApp">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M20.5 3.5A11.7 11.7 0 0 0 12.2 0C5.8 0 .6 5.2.6 11.6c0 2 .5 4 1.5 5.7L.5 23l5.8-1.5a11.6 11.6 0 0 0 5.9 1.6h.1c6.4 0 11.6-5.2 11.6-11.6a11.5 11.5 0 0 0-3.4-8Zm-8.3 17.7h-.1a9.7 9.7 0 0 1-4.9-1.3l-.4-.2-3.4.9.9-3.3-.2-.4a9.7 9.7 0 1 1 8.1 4.3Zm5.3-7.2c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1l-.9 1.1c-.2.2-.4.2-.7.1a7.8 7.8 0 0 1-2.3-1.4 8.5 8.5 0 0 1-1.6-2c-.2-.3 0-.5.1-.6l.5-.6.3-.5c.1-.2 0-.4 0-.6l-.9-2.1c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.4-1.2 1.2-1.2 2.9 0 1.7 1.2 3.3 1.4 3.5.2.2 2.4 3.7 5.9 5.2.8.4 1.5.6 2 .7.8.3 1.6.3 2.2.2.7-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3Z" />
              </svg>
            </span>
          </div>
        </div>
        <FollowUs compact />
      </footer>
    </div>
  );
}

function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}

export default App;
